import { TileLayout } from "@progress/kendo-react-layout";
import { useLocalization } from "@progress/kendo-react-intl";
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon, bellIcon, clipboardIcon} from '@progress/kendo-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignal } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect, useRef, ReactNode } from "react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import AssetMetricsReadDto from "../../../../Models/Dashboard/AssetMetricsReadDto";
import { UseQueryResult } from "react-query";
import AssetImageReadDto from "../../../../Models/Assets/AssetImageReadDto";
import { enMessages } from "../../../../messages/en-US";
import { getStatusColor, STATUS_COLORS } from "../../Utilities/statusColorUtils";

/** Asset metric (compliance / connectivity / alert): the metric icon + a coloured status dot. */
const StatusIndicator = ({ icon, title, color }: { icon: ReactNode; title: string; color: string }) => (
  <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
    <div className="align-items-center" style={{ cursor: 'pointer' }} title={title}>
      {icon}
      <SvgIcon icon={circleIcon} style={{ color }} size="xlarge" />
    </div>
  </Tooltip>
);

interface Props {
  assetsMetricsData: AssetMetricsReadDto[];
  onAssetSelect: (asset: AssetMetricsReadDto) => void;
  imagesQueries: UseQueryResult<AssetImageReadDto>[];
}

export default function AssetsDashboardTileView({ assetsMetricsData, onAssetSelect, imagesQueries }: Props) {
  const localizationService = useLocalization();

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  useEffect(() => {
    const node = containerRef.current;
    const handleResize = () => {
      if (node) {
        setContainerWidth(node.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const observer = new window.ResizeObserver(handleResize);
    if (node) observer.observe(node);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (node) observer.unobserve(node);
    };
  }, []);

  const gap = 10;
  
  // Determine number of columns based on screen size
  const getColumns = () => {
    if (window.innerWidth < 768) return 1; // Mobile: 1 column
    return Math.max(1, Math.floor((containerWidth + gap) / (300 + gap))); // Desktop: multiple columns
  };
  
  const columns = getColumns();
  
  const getColumnWidth = () => {
    if (window.innerWidth < 768) return window.innerWidth - 40; // Mobile: full width
    // Desktop: fill available space
    return (containerWidth - (gap * (columns - 1))) / columns;
  };

  const [columnWidth, setColumnWidth] = useState(getColumnWidth());
  
  // Update column width when container width changes
  useEffect(() => {
    setColumnWidth(getColumnWidth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth]);
  
  // Calculate available width for text based on tile width
  const imageWidth = 120; // Image width
  const padding = 20; // Total padding (9px on each side)
  const margin = 8; // Margin between image and text
  const availableTextWidth = columnWidth - imageWidth - padding - margin;
  
  const dashboardTileData = assetsMetricsData.map((asset) => {
    // Image is optional — a missing/failed image just renders an empty box, never a broken-image glyph.
    const imageSrc = imagesQueries.find(query => query.data?.id === asset.imageId)?.data?.data;
    return {
    body: (
      <div className="p-2 cursor-pointer asset-tile h-full" onClick={() => onAssetSelect(asset)}>
        <div className="flex align-items-center">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={asset.name}
              className="me-2"
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          ) : (
            <div className="me-2" style={{ width: '120px', height: '120px' }} aria-hidden="true" />
          )}
          <div className="mt-3">
            <Tooltip
              anchorElement="target"
              position="bottom"
              parentTitle={true}
            >
              <div
                className="font-bold text-lg"
                style={{
                  maxWidth: `${availableTextWidth}px`,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  display: 'block',
                  cursor: 'pointer'
                }}
                onClick={() => onAssetSelect(asset)}
                title={asset.name}
              >
                {asset.name}
              </div>
            </Tooltip>
            <div className="mb-1">{asset.location}</div>
            <div className="text-xs">{asset.manufacturer}</div>
            <div className="text-xs">{asset.model}</div>
          </div>
        </div>
        <div className="flex justify-evenly mt-3">
          <StatusIndicator
            icon={<SvgIcon icon={clipboardIcon} size="xlarge" />}
            color={getStatusColor(asset.complianceStatus)}
            title={
              `${localizationService.toLanguageString('custom.compliance', enMessages.custom.compliance)}: ${
                asset.complianceStatus === 'Compliant' ? localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant) :
                asset.complianceStatus === 'NonCompliant' ? localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant) :
                asset.complianceStatus === 'PartiallyCompliant' ? localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant) :
                'Unknown'}`
            }
          />
          <StatusIndicator
            icon={<FontAwesomeIcon icon={faSignal} size="xl" />}
            color={getStatusColor(asset.connectivityStatus)}
            title={
              `${localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)}: ${
                asset.connectivityStatus === 'Online' ? localizationService.toLanguageString('custom.online', enMessages.custom.online) :
                asset.connectivityStatus === 'Offline' ? localizationService.toLanguageString('custom.oneOrMoreDevicesAreOffline', enMessages.custom.oneOrMoreDevicesAreOffline) :
                asset.connectivityStatus === 'Partial' ? localizationService.toLanguageString('custom.partialConnected', enMessages.custom.partialConnected) :
                'Unknown'
              }`
            }
          />
          <StatusIndicator
            icon={<SvgIcon icon={bellIcon} size="xlarge" />}
            color={asset.alert?.value > 0 ? STATUS_COLORS.critical : STATUS_COLORS.ok}
            title={`${localizationService.toLanguageString('custom.alerts', enMessages.custom.alerts)}: ${asset.alert?.value ?? 0}`}
          />
        </div>
      </div>
    ),
    reorderable: false,
    };
  });

  useEffect(() => {
    setColumnWidth(getColumnWidth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth]);

  const columnsDisplayPosition = assetsMetricsData.map((_, index) => ({
    col: (index % columns) + 1,
    colSpan: 1,
    rowSpan: 1,
    row: Math.floor(index / columns) + 1
  }));


  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <TileLayout
        columns={columns}
        rowHeight={200}
        columnWidth={columnWidth}
        positions={columnsDisplayPosition}
        gap={{ rows: 10, columns: 10 }}
        items={dashboardTileData}
        style={{padding: 0}}
        className="selected-dashboard-tile-view dashboard-page-tile p-0 border-none shadow-none bg-transparent dashboard-custom-tile-layout"
      />
    </div>
  );
}